import { Link } from '@tanstack/react-router';
import { Plus } from '@phosphor-icons/react';
import type { BusinessRow as Business } from '@mybusinessmate/schema';
import { initialsOf } from '../../lib/initials';
import { RowMenu, type RowMenuItem } from '../directory/row-menu';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';

/**
 * The columns of Figma A03.1 "Card · Table": Business 284, Plan 170, Status 130, Account manager 168, Work 140,
 * Quota 150, Renews 100, actions 42, with 16px before the first. Below `lg` only the business and the menu remain.
 */
export const BUSINESS_ROW_GRID =
  'grid items-center pl-(--mbm-space-4) grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,284fr)_minmax(0,170fr)_minmax(0,130fr)_minmax(0,168fr)_minmax(0,140fr)_minmax(0,150fr)_minmax(0,100fr)_minmax(0,42fr)]';

const HEAD = 'text-label-small text-text-tertiary uppercase';

export function BusinessRowsHeader() {
  return (
    <div
      className={`${BUSINESS_ROW_GRID} hidden h-(--mbm-space-8) border-b border-border-line bg-surface-canvas lg:grid`}
    >
      <span className={HEAD}>Business</span>
      <span className={HEAD}>Plan</span>
      <span className={HEAD}>Status</span>
      <span className={HEAD}>Account manager</span>
      <span className={HEAD}>Work</span>
      <span className={HEAD}>Quota</span>
      <span className={HEAD}>Renews</span>
      <span aria-hidden />
    </div>
  );
}

/** A value the design shows but billing (Sprint 4) has not supplied yet. */
const Pending = () => <span className="text-body-small text-text-tertiary">—</span>;

/**
 * A business row (Figma A03.1).
 * TODO(api): Plan, Status, Quota and Renews have no data until billing exists (Sprint 4). They keep their columns, as
 * designed, and show a dash; the status chips (Active / Grace period / Cancelling), quota bar and the red "Failed
 * 16 Oct · retry 2 of 3" renewal state are built when that data arrives.
 */
export function BusinessRow({
  business,
  canAssign,
  onAssign,
}: {
  business: Business;
  /** Only admins assign account managers. */
  canAssign: boolean;
  onAssign: () => void;
}) {
  const menu: RowMenuItem[] = canAssign
    ? [{ label: business.accountManager ? 'Change account manager' : 'Assign account manager', onSelect: onAssign }]
    : [];
  const owner = [business.ownerName, business.city].filter(Boolean).join(' · ');

  return (
    <div
      className={`${BUSINESS_ROW_GRID} h-(--mbm-space-9) border-b border-border-line transition-colors duration-(--mbm-duration-fast) hover:bg-surface-muted`}
    >
      <Link
        to="/subdomain/admin/businesses/$businessId"
        params={{ businessId: business.id }}
        className="flex min-w-0 items-center gap-(--mbm-space-2-5) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        <span
          aria-hidden
          className="flex size-(--mbm-size-avatar-sm) shrink-0 items-center justify-center rounded-(--mbm-component-radius-table-avatar) bg-surface-field text-label-small text-text-primary"
        >
          {initialsOf(business.name)}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-label-medium text-text-primary">{business.name}</span>
          <span className="truncate text-body-xsmall text-text-tertiary">{owner}</span>
        </span>
      </Link>

      <span className="hidden lg:block">
        <Pending />
      </span>
      <span className="hidden lg:block">
        <Pending />
      </span>

      <span className="hidden lg:block">
        {business.accountManager ? (
          <span className="flex items-center gap-(--mbm-space-2-5) text-body-small text-text-primary">
            <Avatar
              tone="neutral"
              initials={initialsOf(business.accountManager.name)}
              size="xxs"
              label={business.accountManager.name}
            />
            {business.accountManager.name}
            {business.accountManagerCount > 1 ? (
              <span className="text-caption text-text-tertiary">+{business.accountManagerCount - 1}</span>
            ) : null}
          </span>
        ) : canAssign ? (
          <button
            type="button"
            onClick={onAssign}
            className="inline-flex items-center gap-(--mbm-space-2) text-label-medium text-brand-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={Plus} size="sm" />
            Assign
          </button>
        ) : (
          <span className="text-body-small text-text-tertiary">None yet</span>
        )}
      </span>

      <span className="hidden flex-col lg:flex">
        <span className="text-body-small text-text-primary">{business.openTaskCount} open</span>
        {business.waitingForClientCount > 0 ? (
          <span className="text-caption text-text-tertiary">{business.waitingForClientCount} waiting for client</span>
        ) : null}
      </span>

      <span className="hidden lg:block">
        <Pending />
      </span>
      <span className="hidden lg:block">
        <Pending />
      </span>

      <span className="flex justify-end pr-(--mbm-space-3)">
        <RowMenu label={`Actions for ${business.name}`} items={menu} />
      </span>
    </div>
  );
}

/** A loading row in the same grid (Figma "Row/Skeleton"): a tile and one bar per column. */
export function BusinessRowSkeleton() {
  return (
    <div className={`${BUSINESS_ROW_GRID} h-(--mbm-space-9) border-b border-border-line`}>
      <span className="flex items-center gap-(--mbm-space-2-5)">
        <Skeleton className="size-(--mbm-size-avatar-sm) rounded-(--mbm-component-radius-table-avatar)" />
        <Skeleton className="h-(--mbm-space-2-5) w-1/2" />
      </span>
      {Array.from({ length: 6 }, (_, i) => (
        <span key={i} className="hidden lg:block">
          <Skeleton className="h-(--mbm-space-2-5) w-2/3" />
        </span>
      ))}
      <span />
    </div>
  );
}
